import { useEffect, useMemo, useState } from 'react';
import '../../assets/css/InfluencerIQ/style.css';
import Header from '../../components/Header';
import CampaignInput from '../../components/InfluencerIQ/CampaignInput';
import CampaignPerformance from '../../components/InfluencerIQ/CampaignPerformance';
import InfluencerFilters from '../../components/InfluencerIQ/InfluencerFilters';
import InfluencerTable from '../../components/InfluencerIQ/InfluencerTable';
import InfluencerModal from '../../components/InfluencerIQ/InfluencerModal';
import ChatBot from '../../components/InfluencerIQ/ChatBot';
import { fetchBootstrap } from '../../utils/InfluencerIQ/api';

function InfluencerIQ() {
  const [campaignText, setCampaignText] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);

  const [bootstrap, setBootstrap] = useState(null);
  const [bootstrapError, setBootstrapError] = useState(null);

  const [performanceData, setPerformanceData] = useState(null);
  const [allInfluencers, setAllInfluencers] = useState([]);

  const [filters, setFilters] = useState({
    objective: 'All',
    platform: 'All',
    geography: 'All',
    risk: 'All'
  });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const data = await fetchBootstrap();
        if (cancelled) return;
        setBootstrap(data);
        setBootstrapError(null);

        const defaultBrand = data?.campaignResultsData?.Bisquick ? 'Bisquick' : Object.keys(data?.campaignResultsData || {})[0];
        if (defaultBrand) {
          setPerformanceData(data.campaignResultsData[defaultBrand].performance);
          setAllInfluencers(data.campaignResultsData[defaultBrand].influencers);
        }
      } catch (err) {
        if (cancelled) return;
        setBootstrapError(err?.message || 'Failed to load data');
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const campaignResultsData = bootstrap?.campaignResultsData || {};
  const exampleCampaigns = bootstrap?.exampleCampaigns || [];
  const filterOptions = bootstrap?.filterOptions;

  const canGenerate = useMemo(() => {
    return Boolean(campaignText.trim() && bootstrap && !bootstrapError);
  }, [campaignText, bootstrap, bootstrapError]);

  const handleGenerate = () => {
    if (!campaignText.trim()) return;
    if (!bootstrap || bootstrapError) return;

    setIsLoading(true);
    setShowResults(false);

    // Determine which brand's data to show
    let selectedBrand = "Bisquick"; // Default
    if (campaignText.includes("Betty Crocker")) selectedBrand = "Betty Crocker";
    else if (campaignText.includes("Pillsbury")) selectedBrand = "Pillsbury";
    else if (campaignText.includes("Gold Medal Flour")) selectedBrand = "Gold Medal Flour";

    // Simulate API call delay
    const brandData = campaignResultsData[selectedBrand];
    if (!brandData) {
      setIsLoading(false);
      return;
    }

    setPerformanceData(brandData.performance);
    setAllInfluencers(brandData.influencers);
    setIsLoading(false);
    setShowResults(true);
  };

  const handleViewDetails = (influencer) => {
    setSelectedInfluencer(influencer);
  };

  const handleCloseModal = () => {
    setSelectedInfluencer(null);
  };

  // Filter influencers based on selected filters
  const filteredInfluencers = allInfluencers.filter(influencer => {
    // Platform filter
    if (filters.platform !== 'All' && influencer.platform !== filters.platform) {
      return false;
    }

    // Objective filter
    if (filters.objective !== 'All' && influencer.objective !== filters.objective) {
      return false;
    }

    // Geography filter
    if (filters.geography !== 'All' && influencer.geography !== filters.geography) {
      return false;
    }

    // Risk filter
    if (filters.risk !== 'All') {
      if (filters.risk === 'Low' && influencer.risk !== 'Safe') return false;
      if (filters.risk === 'Moderate' && influencer.risk === 'High') return false;
      if (filters.risk === 'High' && influencer.risk !== 'Moderate') return false;
    }

    return true;
  }).slice(0, 10); // Limit to 10 influencers

  return (
    <div className="app influenceriq-app">
      <Header productLabel="InfluencerIQ" fixed={true} />

      <main className="main-content">
        <CampaignInput
          campaignText={campaignText}
          setCampaignText={setCampaignText}
          onGenerate={handleGenerate}
          isLoading={isLoading}
          exampleCampaigns={exampleCampaigns}
        />

        {bootstrapError && (
          <div className="loading-overlay">
            <p className="loading-text">
              Failed to load data from backend: {bootstrapError}
            </p>
          </div>
        )}

        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner" />
            <p className="loading-text">Analyzing influencers for your campaign...</p>
          </div>
        )}

        {showResults && !isLoading && performanceData && (
          <div className="results-section">
            <CampaignPerformance data={performanceData} />

            <div className="influencers-section">
              <h2 className="influencers-title">Recommended Influencers</h2>

              <InfluencerFilters
                filters={filters}
                setFilters={setFilters}
                filterOptions={filterOptions}
              />

              <InfluencerTable
                influencers={filteredInfluencers}
                onViewDetails={handleViewDetails}
              />
            </div>
          </div>
        )}
      </main>

      {selectedInfluencer && (
        <InfluencerModal
          influencer={selectedInfluencer}
          onClose={handleCloseModal}
        />
      )}

      <ChatBot />
    </div>
  );
}

export default InfluencerIQ;
